import React from 'react';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { useFeatures } from '../hooks/useFeatures';
import { useAuth } from '../contexts/AuthContext';

/**
 * <FeatureGate name="ai_petronille">
 *   <RealUI />
 * </FeatureGate>
 *
 * - When the module is enabled, renders children as-is (zero overhead post-load).
 * - When disabled, renders an inline upsell card by default. Pass `mode="hidden"`
 *   to hide entirely, or `fallback={<X/>}` for a custom replacement.
 * - During the initial features fetch, children are rendered optimistically
 *   to avoid layout flicker — the backend's `assert_feature` is still the
 *   real enforcement layer.
 */
export default function FeatureGate({
  name,
  children,
  mode = 'upsell',  // 'upsell' | 'hidden' | 'disabled'
  fallback = null,
  description = null,
  testId = null,
}) {
  const { has, loading, catalog, plan } = useFeatures();
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase().replace(' ', '_');
  const isSuperAdmin = role === 'super_admin' || role === 'superadmin';

  if (loading) return children;
  if (has(name)) return children;

  if (mode === 'hidden') return null;
  if (mode === 'disabled') return (
    <div className="opacity-50 pointer-events-none select-none" aria-disabled="true" data-testid={testId || `feature-locked-${name}`}>
      {children}
    </div>
  );
  if (fallback) return fallback;

  // Default — inline upsell card
  const meta = catalog.find((m) => m.key === name);
  const label = meta?.label || name;
  const desc = description || meta?.description || 'Cette fonctionnalité fait partie d\'un forfait supérieur.';
  return (
    <div
      data-testid={testId || `feature-gate-${name}`}
      className="rounded-2xl border-2 border-dashed border-[#4F46E5]/30 bg-gradient-to-br from-[#EEF2FF] to-white p-5 flex items-start gap-3"
    >
      <div className="w-10 h-10 rounded-xl bg-[#4F46E5]/15 flex items-center justify-center shrink-0">
        <Lock className="w-4 h-4 text-[#4F46E5]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles className="w-3.5 h-3.5 text-[#4F46E5]" />
          <h3 className="text-[14px] font-bold text-[#1A1B2E]">{label}</h3>
          <span className="ml-auto text-[10.5px] uppercase tracking-wide text-[#4F46E5] font-bold bg-white px-2 py-0.5 rounded-full border border-[#4F46E5]/20">
            {meta?.category || 'Pro'}
          </span>
        </div>
        <p className="text-[12.5px] text-[#4A4B63] leading-relaxed mb-3">{desc}</p>
        <button
          data-testid={`feature-upsell-${name}`}
          onClick={() => {
            if (isSuperAdmin) {
              window.location.href = '/superadmin';
            } else {
              window.location.href = `mailto:contact@ifaap.fr?subject=${encodeURIComponent(`Activation du module ${label}`)}&body=${encodeURIComponent(`Bonjour,\n\nJe souhaite activer le module « ${label} » pour mon organisme.\nForfait actuel : ${plan || 'Starter'}.\n\nMerci.`)}`;
            }
          }}
          className="h-8 px-3 rounded-lg brand-gradient text-white text-[12px] font-semibold inline-flex items-center gap-1.5 shadow-sm"
        >
          {isSuperAdmin ? 'Gérer mes forfaits' : 'Demander l\'activation'}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
